import axios from 'axios';
import { getAuthHeader } from './authService';

export interface Team {
  id: number;
  departmentId: number;
  departmentName?: string;
  name: string;
  logo?: string;
  location?: string;
  email?: string;
  phone?: string;
  universityId: number;
  createdBy: number;
  createdByType: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  membershipStatus?: string;
}

export interface TeamMembershipRequest {
  teamId: number;
  studentId: number;
  action: string;
}

class TeamService {
  private readonly API_URL = process.env.REACT_APP_API_BASE_URL || '/api';

  async getMyTeams(): Promise<Team[]> {
    try {
      // Check user type from stored auth data
      const authData = localStorage.getItem('auth');
      const userType = authData ? JSON.parse(authData).userType : null;
      
      let endpoint = `${this.API_URL}/teams`;
      
      // Use different endpoints based on user type
      if (userType === 'student') {
        endpoint = `${this.API_URL}/student-teams/joined`;
      }
      
      const response = await axios.get(endpoint, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error fetching teams:', error);
      throw error;
    }
  }

  async getTeamById(id: number): Promise<Team> {
    try {
      const response = await axios.get(
        `${this.API_URL}/teams/${id}`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching team:', error);
      throw error;
    }
  }

  async activateTeam(id: number): Promise<void> {
    try {
      await axios.post(
        `${this.API_URL}/teams/${id}/activate`,
        {},
        { headers: getAuthHeader() }
      );
    } catch (error) {
      console.error('Error activating team:', error);
      throw error;
    }
  }

  async deactivateTeam(id: number): Promise<void> {
    try {
      await axios.post(
        `${this.API_URL}/teams/${id}/deactivate`,
        {},
        { headers: getAuthHeader() }
      );
    } catch (error) {
      console.error('Error deactivating team:', error);
      throw error;
    }
  }

  async requestTeamAccess(request: TeamMembershipRequest): Promise<void> {
    try {
      await axios.post(
        `${this.API_URL}/teams/request-access`,
        request,
        { headers: getAuthHeader() }
      );
    } catch (error) {
      console.error('Error requesting team access:', error);
      throw error;
    }
  }

  async getStudentTeams(studentId: number): Promise<Team[]> {
    try {
      const response = await axios.get(
        `${this.API_URL}/teams/student/${studentId}`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching student teams:', error);
      throw error;
    }
  }
}

const teamService = new TeamService();
export default teamService;